import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from './ui/sheet.jsx';
import { Button } from './ui/button.jsx';
import { Badge } from './ui/badge.jsx';
import {
    AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import {
    ShieldCheck, ShieldAlert, ShieldQuestion, RotateCcw, Loader2, Activity,
} from 'lucide-react';
import { parseUtc } from '../lib/parseUtc';

/**
 * SLOImpactPanel — drill-down side-drawer for one rightsizing action.
 *
 * Fetches /rightsizing/actions/{action_id}/slo-impact and renders:
 *   - confidence gauge (0–100, color-coded per slo_guard thresholds)
 *   - two sparklines: latency p95 and error rate, baseline vs +5min/+15min
 *   - delta table per checkpoint
 *   - manual rollback CTA (only when verdict is regression and action
 *     hasn't already been auto-rolled-back)
 *
 * Driven from OptimizationHistory rows via the "View" link.
 */
export default function SLOImpactPanel({ actionId, open, onOpenChange }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [rollingBack, setRollingBack] = useState(false);

    useEffect(() => {
        if (!open || !actionId) return;
        let cancelled = false;
        async function load() {
            setLoading(true);
            setError(null);
            try {
                const resp = await axios.get(`${API}/rightsizing/actions/${actionId}/slo-impact`);
                if (!cancelled) setData(resp.data);
            } catch (e) {
                if (!cancelled) setError(e.response?.data?.detail || e.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => { cancelled = true; };
    }, [actionId, open]);

    const handleRollback = async () => {
        if (!actionId) return;
        if (!window.confirm('Roll back this rightsizing change to the previous resources?')) return;
        setRollingBack(true);
        try {
            await axios.post(`${API}/agent/rollback/${actionId}`);
            window.alert('Rollback queued. The agent will revert this change in the next cycle.');
            onOpenChange(false);
        } catch (e) {
            window.alert(`Rollback failed: ${e.response?.data?.detail || e.message}`);
        } finally {
            setRollingBack(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
                <SheetHeader>
                    <SheetTitle className="flex items-center gap-2">
                        <Activity className="w-5 h-5 text-indigo-600" />
                        SLO Impact
                    </SheetTitle>
                    <SheetDescription>
                        Golden-signal verification for this rightsizing apply.
                    </SheetDescription>
                </SheetHeader>

                <div className="mt-6 space-y-6">
                    {loading && (
                        <div className="flex items-center gap-2 text-slate-500 text-sm">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Loading SLO data…
                        </div>
                    )}
                    {error && (
                        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
                            {error}
                        </div>
                    )}
                    {data && !loading && (
                        <>
                            <Header data={data} />
                            <ConfidenceGauge data={data} />
                            <Sparklines data={data} />
                            <DeltaTable data={data} />
                            <RollbackCTA
                                data={data}
                                rollingBack={rollingBack}
                                onRollback={handleRollback}
                            />
                        </>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
}

// ---------- subcomponents ----------

function Header({ data }) {
    return (
        <div>
            <div className="text-sm font-medium text-slate-900">
                {data.namespace} / {data.workload_name}
            </div>
            <div className="text-xs text-slate-500 mt-1">
                Applied {data.completed_at ? parseUtc(data.completed_at).toLocaleString() : '—'}
            </div>
        </div>
    );
}

function ConfidenceGauge({ data }) {
    const c = data.slo_confidence;
    let tone, Icon, label;
    if (data.slo_status === 'auto_rolled_back') {
        tone = 'text-orange-700 bg-orange-50 border-orange-200';
        Icon = ShieldAlert;
        label = 'Auto-rolled back';
    } else if (c == null) {
        tone = 'text-gray-700 bg-gray-50 border-gray-200';
        Icon = ShieldQuestion;
        label = 'SLO data unavailable';
    } else if (c >= 90) {
        tone = 'text-green-700 bg-green-50 border-green-200';
        Icon = ShieldCheck;
        label = 'Verified safe';
    } else if (c >= 70) {
        tone = 'text-yellow-800 bg-yellow-50 border-yellow-200';
        Icon = ShieldAlert;
        label = 'Within tolerance';
    } else {
        tone = 'text-red-700 bg-red-50 border-red-200';
        Icon = ShieldAlert;
        label = 'Regression detected';
    }
    return (
        <div className={`rounded-lg border p-4 ${tone}`}>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{label}</span>
                </div>
                <div className="text-3xl font-semibold">
                    {c != null ? c : '—'}
                </div>
            </div>
            {data.slo_rollback_reason && (
                <div className="text-xs mt-2 italic">{data.slo_rollback_reason}</div>
            )}
        </div>
    );
}

function Sparklines({ data }) {
    const baseline = data.slo_baseline?.samples || {};
    const post = data.slo_post_apply || [];
    if (post.length === 0) {
        return (
            <div className="text-sm text-slate-500 italic">
                No post-apply checkpoints recorded yet. Re-open this panel after +15min.
            </div>
        );
    }

    // Build series for each metric: index 0 = baseline, then one point per checkpoint.
    const buildSeries = (key) => {
        const baseVal = baseline[key];
        const points = [{ label: 'Baseline', value: typeof baseVal === 'number' ? baseVal : null }];
        for (const cp of post) {
            const v = cp.samples?.[key];
            points.push({
                label: `+${cp.minutes_since_apply}min`,
                value: typeof v === 'number' ? v : null,
            });
        }
        return points;
    };

    return (
        <div className="space-y-4">
            <SparklineChart
                title="Latency p95 (ms)"
                data={buildSeries('latency_p95_ms')}
                color="#6366f1"
            />
            <SparklineChart
                title="Error rate"
                data={buildSeries('error_rate')}
                color="#ef4444"
            />
        </div>
    );
}

function SparklineChart({ title, data, color }) {
    return (
        <div>
            <div className="text-xs font-medium text-slate-700 mb-1">{title}</div>
            <div className="h-24 w-full">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                        <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} width={40} />
                        <Tooltip formatter={(v) => (v == null ? '—' : v.toFixed(3))} />
                        <Area
                            type="monotone"
                            dataKey="value"
                            stroke={color}
                            fill={color}
                            fillOpacity={0.15}
                        />
                        <ReferenceLine x="Baseline" stroke="#94a3b8" strokeDasharray="3 3" />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

function DeltaTable({ data }) {
    const post = data.slo_post_apply || [];
    if (post.length === 0) return null;
    return (
        <div>
            <div className="text-xs font-medium text-slate-700 mb-1">Per-checkpoint deltas</div>
            <table className="w-full text-xs border border-slate-200 rounded">
                <thead className="bg-slate-50">
                    <tr>
                        <th className="text-left px-2 py-1 font-medium text-slate-600">Checkpoint</th>
                        <th className="text-left px-2 py-1 font-medium text-slate-600">Latency p95</th>
                        <th className="text-left px-2 py-1 font-medium text-slate-600">Errors</th>
                        <th className="text-left px-2 py-1 font-medium text-slate-600">Throughput</th>
                        <th className="text-left px-2 py-1 font-medium text-slate-600">Verdict</th>
                    </tr>
                </thead>
                <tbody>
                    {post.map((cp, i) => (
                        <tr key={i} className="border-t border-slate-200">
                            <td className="px-2 py-1">+{cp.minutes_since_apply}min</td>
                            <td className="px-2 py-1">{fmtPct(cp.delta_pct?.latency_p95_ms)}</td>
                            <td className="px-2 py-1">{fmtAbs(cp.delta_pct?.error_rate)}</td>
                            <td className="px-2 py-1">{fmtPct(cp.delta_pct?.throughput_rps)}</td>
                            <td className="px-2 py-1">
                                <VerdictPill verdict={cp.verdict} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function VerdictPill({ verdict }) {
    const tone = {
        within_tolerance: 'bg-green-100 text-green-800',
        improvement: 'bg-green-100 text-green-800',
        regression: 'bg-red-100 text-red-800',
        unavailable: 'bg-gray-100 text-gray-700',
    }[verdict] || 'bg-gray-100 text-gray-700';
    return <Badge className={`text-[10px] ${tone}`}>{verdict}</Badge>;
}

function RollbackCTA({ data, rollingBack, onRollback }) {
    if (data.slo_status === 'auto_rolled_back') {
        return (
            <div className="text-xs text-slate-500 italic">
                This change was auto-rolled-back at{' '}
                {data.slo_rollback_at ? parseUtc(data.slo_rollback_at).toLocaleString() : 'unknown time'}.
            </div>
        );
    }
    const latest = (data.slo_post_apply || []).slice(-1)[0];
    const isRegression = latest?.verdict === 'regression';
    return (
        <div className="pt-2 border-t border-slate-200">
            <Button
                onClick={onRollback}
                disabled={rollingBack}
                variant={isRegression ? 'destructive' : 'outline'}
                className="w-full flex items-center justify-center gap-2"
            >
                {rollingBack ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                    <RotateCcw className="w-4 h-4" />
                )}
                {isRegression ? 'Rollback (SLO regression)' : 'Manual rollback'}
            </Button>
            {!isRegression && (
                <div className="text-[11px] text-slate-500 mt-2 text-center">
                    No regression detected — manual rollback only if you have other reasons.
                </div>
            )}
        </div>
    );
}

function fmtPct(v) {
    if (v == null) return '—';
    const sign = v >= 0 ? '+' : '';
    return `${sign}${v.toFixed(1)}%`;
}

function fmtAbs(v) {
    if (v == null) return '—';
    const sign = v >= 0 ? '+' : '';
    return `${sign}${v.toFixed(4)}`;
}
